import {
  App,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { TableProps } from 'antd';
import { CheckCircleFilled, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { setupApi, type AcademicYear } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const QK = ['setup', 'academic-years'] as const;

interface FormShape {
  name: string;
  range: [Dayjs, Dayjs];
  makeCurrent: boolean;
}

export function AcademicYearsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('SETUP:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('SETUP:EDIT'));

  const [editing, setEditing] = useState<AcademicYear | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: QK,
    queryFn: setupApi.listAcademicYears,
  });

  const upsert = useMutation({
    mutationFn: async (values: FormShape) => {
      const body = {
        name: values.name.trim(),
        startDate: values.range[0]!.format('YYYY-MM-DD'),
        endDate: values.range[1]!.format('YYYY-MM-DD'),
        makeCurrent: values.makeCurrent,
      };
      return editing
        ? setupApi.updateAcademicYear(editing.id, body)
        : setupApi.createAcademicYear(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Academic year updated' : 'Academic year created');
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
      setOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const makeCurrent = useMutation({
    mutationFn: setupApi.makeYearCurrent,
    onSuccess: () => {
      message.success('Marked as current academic year');
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ makeCurrent: false });
    setOpen(true);
  };

  const openEdit = (row: AcademicYear) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      range: [dayjs(row.startDate), dayjs(row.endDate)],
      makeCurrent: row.current,
    });
    setOpen(true);
  };

  const columns: TableProps<AcademicYear>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name: string, row) => (
        <Space>
          <span style={{ fontWeight: 600 }}>{name}</span>
          {row.current && (
            <Tag color="processing" icon={<CheckCircleFilled />}>
              Current
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Starts',
      dataIndex: 'startDate',
      width: 140,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Ends',
      dataIndex: 'endDate',
      width: 140,
      render: (d: string) => dayjs(d).format('D MMM YYYY'),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      width: 110,
      render: (active: boolean) =>
        active ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: 'Actions',
      width: 220,
      align: 'right',
      render: (_, row) => (
        <Space>
          {!row.current && canEdit && (
            <Tooltip title="Mark this year as current">
              <Button
                size="small"
                onClick={() => makeCurrent.mutate(row.id)}
                loading={makeCurrent.isPending && makeCurrent.variables === row.id}
              >
                Make current
              </Button>
            </Tooltip>
          )}
          {canEdit && (
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            >
              Edit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Setup / Academic"
        title="Academic years"
        description="Each session has a name and a date range. Exactly one year is marked current at a time."
        count={{ value: data?.length ?? 0, label: 'years' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New academic year
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New academic year'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => upsert.mutate(values)}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: 'Name is required' },
              { max: 50 },
            ]}
          >
            <Input placeholder="e.g. 2026-27" autoFocus />
          </Form.Item>
          <Form.Item
            label="Date range"
            name="range"
            rules={[{ required: true, message: 'Pick start and end dates' }]}
          >
            <DatePicker.RangePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item
            label="Mark as current"
            name="makeCurrent"
            valuePropName="checked"
            tooltip="Clears the current flag on every other year."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
